'use client';

import { produce } from 'immer';
import { FC, ReactNode, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import { storeFactory } from 'src/stores/storeFactory';

export interface StepFormStoreStateData {
  /** Display a modal or dialog when attempting top exit form */
  preventExit: boolean;
  /** Handles the state of the exit modal */
  showExitModal: boolean;
  /** Set the exit URL */
  exitUrl: string;
  /** Current form progress */
  formProgress: number | null;
  /** Previous url or handler */
  onBack?: (() => void) | string;
}

interface StepFormStoreStateFunctions {
  setPreventExit: (preventExit: StepFormStoreStateData['preventExit']) => void;
  setShowExitModal: (
    showExitModal: StepFormStoreStateData['showExitModal'],
    exitUrl?: StepFormStoreStateData['exitUrl'],
  ) => void;
  setFormProgressWithPreviousUrl: ({
    formProgress,
    onBack,
  }: {
    formProgress: StepFormStoreStateData['formProgress'];
    onBack?: (() => void) | string;
  }) => void;
}

const {
  store: stepFormStore,
  StoreProvider: StepFormStoreProvider,
  useCreateStore: useCreateStepFormStore,
} = storeFactory<StepFormStoreStateData, StepFormStoreStateFunctions>(
  () => ({
    preventExit: false,
    showExitModal: false,
    exitUrl: '/',
    formProgress: null,
    onBack: '',
  }),
  (set) => ({
    setPreventExit: (preventExit: StepFormStoreStateData['preventExit']) => {
      set((state) =>
        produce(state, (draft) => {
          draft.preventExit = preventExit;
        }),
      );
    },
    setShowExitModal: (
      showExitModal: StepFormStoreStateData['preventExit'],
      exitUrl: StepFormStoreStateData['exitUrl'] = '/',
    ) => {
      set((state) =>
        produce(state, (draft) => {
          draft.showExitModal = showExitModal;
          draft.exitUrl = exitUrl;
        }),
      );
    },
    setFormProgressWithPreviousUrl: ({
      formProgress,
      onBack,
    }: {
      formProgress: StepFormStoreStateData['formProgress'];
      onBack?: (() => void) | string;
    }) => {
      set((state) =>
        produce(state, (draft) => {
          draft.formProgress = formProgress;
          draft.onBack = onBack;
        }),
      );
    },
  }),
);

export { stepFormStore, StepFormStoreProvider, useCreateStepFormStore };

export const StepFormStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createStepFormStore = useCreateStepFormStore({});

  return <StepFormStoreProvider createStore={createStepFormStore}>{children}</StepFormStoreProvider>;
};

export const StepFormSetPreventExit: FC<Pick<StepFormStoreStateData, 'preventExit'>> = ({ preventExit }) => {
  const { preventExit: preventExitContext, setPreventExit } = stepFormStore(
    (state) => ({
      preventExit: state.preventExit,
      setPreventExit: state.setPreventExit,
    }),
    shallow,
  );

  useEffect(() => {
    if (preventExitContext !== preventExit) {
      setPreventExit(preventExit);
    }
  }, [preventExit]);

  return null;
};

export const StepFormSetProgressWithPreviousUrl: FC<Pick<StepFormStoreStateData, 'formProgress' | 'onBack'>> = ({
  formProgress,
  onBack,
}) => {
  const {
    formProgress: formProgressContext,
    setFormProgressWithPreviousUrl,
    onBack: onBackContext,
  } = stepFormStore(
    (state) => ({
      formProgress: state.formProgress,
      onBack: state.onBack,
      setFormProgressWithPreviousUrl: state.setFormProgressWithPreviousUrl,
    }),
    shallow,
  );

  useEffect(() => {
    if (formProgressContext !== formProgress || onBack !== onBackContext || typeof onBack === 'function') {
      setFormProgressWithPreviousUrl({ formProgress, onBack });
    }
  }, [formProgress, onBack]);

  return null;
};
